import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useLegalSettings, replacePlaceholders } from '../hooks/useLegalSettings';

export function PrivacyPage() {
  const { data: settings, isLoading } = useLegalSettings();

  // Replace placeholders in the database content
  const processedContent = useMemo(() => {
    if (!settings?.privacy_content) return null;
    return replacePlaceholders(settings.privacy_content, settings);
  }, [settings]);

  // Fallback values for static content
  const contactEmail = settings?.email || '[E-Mail wird eingerichtet]';
  const companyName = settings?.company_name || settings?.owner_name || 'ParentPath';
  const dpoEmail = settings?.data_protection_officer_email || settings?.email || '[E-Mail wird eingerichtet]';
  const dpoName = settings?.data_protection_officer;
  const fullAddress = settings
    ? `${settings.street || ''}, ${settings.zip_code || ''} ${settings.city || ''}`.trim().replace(/^,\s*/, '')
    : '[Adresse wird eingerichtet]';

  // Get last updated date
  const lastUpdated = settings?.content_updated_at
    ? new Date(settings.content_updated_at).toLocaleDateString('de-DE', {
        month: 'long',
        year: 'numeric',
      })
    : 'Dezember 2024';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-white" />
      </div>
    );
  }

  // If we have database content, render it as HTML
  if (processedContent) {
    return (
      <div className="space-y-4 pb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Datenschutzerklärung</h1>
          <p className="text-white/60 mt-1">Stand: {lastUpdated}</p>
        </div>

        {/* Render HTML content from database */}
        <div
          className="legal-content prose prose-invert prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: processedContent }}
        />
      </div>
    );
  }

  // Static fallback content
  return (
    <div className="space-y-4 pb-8">
      <div>
        <h1 className="text-2xl font-bold text-white">Datenschutzerklärung</h1>
        <p className="text-white/60 mt-1">Stand: {lastUpdated}</p>
      </div>

      <div className="space-y-4">
        {/* 1. Verantwortlicher */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">1. Verantwortlicher</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>Verantwortlich für die Datenverarbeitung ist:</p>
            <p className="font-medium text-white">{companyName}</p>
            <p>{fullAddress}</p>
            <p>E-Mail: <a href={`mailto:${contactEmail}`} className="text-pp-orange hover:underline">{contactEmail}</a></p>
            {dpoName && (
              <p className="mt-2">
                <strong className="text-white">Datenschutzbeauftragter:</strong> {dpoName}<br />
                E-Mail: <a href={`mailto:${dpoEmail}`} className="text-pp-orange hover:underline">{dpoEmail}</a>
              </p>
            )}
          </div>
        </div>

        {/* 2. Datenverarbeitung */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">2. Welche Daten wir erheben</h2>
          <div className="text-white/80 space-y-3 text-sm">
            <p>Wir erheben und verarbeiten folgende personenbezogene Daten:</p>

            <div>
              <p className="font-medium text-white">Account-Daten:</p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>E-Mail-Adresse (für Login und Kommunikation)</li>
                <li>Name (optional, für personalisierte Ansprache)</li>
                <li>Geburtsdatum (für altersbasierte Funktionen)</li>
                <li>Geschlecht (für relevante Inhalte)</li>
              </ul>
            </div>

            <div>
              <p className="font-medium text-white">Schwangerschafts-Daten:</p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>Errechneter Geburtstermin</li>
                <li>Schwangerschaftswoche</li>
                <li>Anzahl der Kinder (Einling/Mehrlinge)</li>
                <li>Risikoschwangerschafts-Status</li>
                <li>Baby-Namen (wenn eingegeben)</li>
              </ul>
            </div>

            <div>
              <p className="font-medium text-white">Nutzungsdaten:</p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>Checklisten und deren Status</li>
                <li>Kalendereinträge und Termine</li>
                <li>Namen-Swipes und Favoriten</li>
                <li>Chat-Verläufe mit dem KI-Assistenten</li>
                <li>Produktprüfungs-Verlauf</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 3. Rechtsgrundlagen */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">3. Rechtsgrundlagen</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>Die Verarbeitung Ihrer Daten erfolgt auf Basis folgender Rechtsgrundlagen:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong className="text-white">Art. 6 Abs. 1 lit. a DSGVO</strong> – Einwilligung (z.B. für KI-Chat, Produktprüfung)</li>
              <li><strong className="text-white">Art. 6 Abs. 1 lit. b DSGVO</strong> – Vertragserfüllung (für die App-Nutzung)</li>
              <li><strong className="text-white">Art. 6 Abs. 1 lit. f DSGVO</strong> – Berechtigtes Interesse (für App-Verbesserungen)</li>
            </ul>
          </div>
        </div>

        {/* 4. Drittanbieter */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">4. Drittanbieter und Datenübermittlung</h2>
          <div className="text-white/80 space-y-4 text-sm">
            <p>Wir nutzen folgende Dienstleister zur Bereitstellung unserer App:</p>

            <div className="space-y-3">
              <div className="p-3 bg-white/10 rounded-[10px]">
                <p className="font-medium text-white">Supabase (Datenbank & Authentifizierung)</p>
                <p className="text-white/60">Standort: EU (Frankfurt, Deutschland)</p>
                <p className="text-white/60">Zweck: Speicherung aller App-Daten, Benutzerauthentifizierung</p>
              </div>

              <div className="p-3 bg-white/10 rounded-[10px]">
                <p className="font-medium text-white">Vercel (Hosting)</p>
                <p className="text-white/60">Standort: EU / USA</p>
                <p className="text-white/60">Zweck: Bereitstellung der Web-Anwendung</p>
              </div>

              <div className="p-3 bg-white/10 rounded-[10px]">
                <p className="font-medium text-white">Stripe (Zahlungsabwicklung)</p>
                <p className="text-white/60">Standort: USA (Privacy Shield / SCCs)</p>
                <p className="text-white/60">Zweck: Abwicklung von Premium-Abonnements</p>
                <p className="text-white/60">Hinweis: Zahlungsdaten werden direkt bei Stripe verarbeitet</p>
              </div>

              <div className="p-3 bg-white/10 rounded-[10px]">
                <p className="font-medium text-white">Anthropic (KI-Assistent)</p>
                <p className="text-white/60">Standort: USA (SCCs)</p>
                <p className="text-white/60">Zweck: Beantwortung von Fragen im KI-Chat</p>
                <p className="text-white/60">Hinweis: Chat-Nachrichten werden verschlüsselt gespeichert</p>
              </div>

              <div className="p-3 bg-white/10 rounded-[10px]">
                <p className="font-medium text-white">Open Food Facts (Produktdaten)</p>
                <p className="text-white/60">Standort: EU (Frankreich)</p>
                <p className="text-white/60">Zweck: Abruf von Produktinformationen für den Produktcheck</p>
              </div>
            </div>

            <p className="text-white/60">
              Bei Übermittlungen in die USA erfolgt die Datenübertragung auf Basis von
              Standardvertragsklauseln (SCCs) gemäß Art. 46 Abs. 2 lit. c DSGVO.
            </p>
          </div>
        </div>

        {/* 5. Speicherdauer */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">5. Speicherdauer</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <ul className="list-disc list-inside space-y-1">
              <li><strong className="text-white">Account-Daten:</strong> Bis zur Kontolöschung + 30 Tage</li>
              <li><strong className="text-white">Schwangerschafts-Daten:</strong> 1 Jahr nach errechnetem Geburtstermin</li>
              <li><strong className="text-white">KI-Chat-Nachrichten:</strong> 1 Jahr (verschlüsselt)</li>
              <li><strong className="text-white">Zahlungsdaten:</strong> 10 Jahre (gesetzliche Aufbewahrungspflicht)</li>
              <li><strong className="text-white">Nutzungsstatistiken:</strong> 90 Tage (anonymisiert)</li>
            </ul>
            <p className="mt-2">
              Nach Ablauf der Fristen werden die Daten automatisch gelöscht oder anonymisiert.
            </p>
          </div>
        </div>

        {/* 6. Ihre Rechte */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">6. Ihre Rechte</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>Sie haben folgende Rechte bezüglich Ihrer personenbezogenen Daten:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong className="text-white">Auskunftsrecht (Art. 15 DSGVO):</strong> Erfahren Sie, welche Daten wir über Sie speichern</li>
              <li><strong className="text-white">Berichtigungsrecht (Art. 16 DSGVO):</strong> Lassen Sie falsche Daten korrigieren</li>
              <li><strong className="text-white">Löschungsrecht (Art. 17 DSGVO):</strong> Lassen Sie Ihre Daten löschen</li>
              <li><strong className="text-white">Einschränkung (Art. 18 DSGVO):</strong> Schränken Sie die Verarbeitung ein</li>
              <li><strong className="text-white">Datenübertragbarkeit (Art. 20 DSGVO):</strong> Erhalten Sie Ihre Daten in einem gängigen Format</li>
              <li><strong className="text-white">Widerspruchsrecht (Art. 21 DSGVO):</strong> Widersprechen Sie der Verarbeitung</li>
            </ul>
            <p className="mt-3">
              Zur Ausübung Ihrer Rechte können Sie eine DSGVO-Anfrage stellen unter:{' '}
              <Link to="/gdpr" className="text-pp-orange hover:underline">DSGVO-Anfrage</Link>
            </p>
            <p>
              Sie haben zudem das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren.
            </p>
          </div>
        </div>

        {/* 7. Datensicherheit */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">7. Datensicherheit</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>Wir setzen folgende technische und organisatorische Maßnahmen ein:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Verschlüsselte Datenübertragung (TLS/SSL)</li>
              <li>Verschlüsselte Speicherung sensibler Daten (AES-256)</li>
              <li>Row Level Security (RLS) für Datenbankzugriffe</li>
              <li>Regelmäßige Sicherheitsupdates</li>
              <li>Hosting in der EU (Supabase Frankfurt)</li>
            </ul>
          </div>
        </div>

        {/* 8. Cookies */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">8. Cookies und Local Storage</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>
              Unsere App verwendet ausschließlich technisch notwendige Cookies und Local Storage
              für die Funktionalität der Anwendung:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong className="text-white">Session-Cookies:</strong> Für die Authentifizierung</li>
              <li><strong className="text-white">Local Storage:</strong> Für Benutzereinstellungen und Offline-Funktionen</li>
            </ul>
            <p className="mt-2">
              Wir setzen keine Tracking-Cookies oder Cookies von Drittanbietern zu Werbezwecken ein.
            </p>
          </div>
        </div>

        {/* 9. Minderjährige */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">9. Minderjährige</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>
              Die Nutzung unserer App ist ab 16 Jahren gestattet. Personen unter 18 Jahren
              benötigen die Zustimmung ihrer Erziehungsberechtigten.
            </p>
            <p>
              Wir erheben wissentlich keine Daten von Kindern unter 16 Jahren. Sollten Sie
              feststellen, dass ein Kind unter 16 Jahren Daten übermittelt hat, kontaktieren
              Sie uns bitte umgehend.
            </p>
          </div>
        </div>

        {/* 10. Änderungen */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">10. Änderungen dieser Datenschutzerklärung</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>
              Wir behalten uns vor, diese Datenschutzerklärung bei Bedarf anzupassen, um sie an
              geänderte Rechtslage oder bei Änderungen unserer Dienste anzupassen.
            </p>
            <p>
              Bei wesentlichen Änderungen werden wir Sie per E-Mail oder In-App-Benachrichtigung
              informieren.
            </p>
          </div>
        </div>

        {/* Kontakt */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">Kontakt</h2>
          <div className="text-white/80 text-sm">
            <p>Bei Fragen zum Datenschutz erreichen Sie uns unter:</p>
            <p className="mt-2">
              E-Mail:{' '}
              <a href={`mailto:${contactEmail}`} className="text-pp-orange hover:underline">
                {contactEmail}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
